// pages/W_HR_R420.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Sorting,
  Export
} from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

export default function W_HR_R420({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear().toString();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}${m}${d}`;
  };

  const formatOracleDateTimeString = (dateString) => {
    if (!dateString || dateString.trim().length < 14) return dateString;
    const trimmedString = dateString.trim();
    const year = trimmedString.substring(0, 4);
    const month = trimmedString.substring(4, 6);
    const day = trimmedString.substring(6, 8);
    const hour = trimmedString.substring(8, 10);
    const minute = trimmedString.substring(10, 12);
    const second = trimmedString.substring(12, 14);
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  };

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const code = user.username.trim().toUpperCase();
      const found = custList.find(item => item.CODE_CD.trim().toUpperCase() === code);
      if (found) {
        setSelectedCust(found.CODE_CD);
      } else {
        setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
      }
    }
  }, [custList, user]);

  useEffect(() => {
    if (!selectedCust) {
      setDeviceList([]);
      setSelectedDevice(null);
      return;
    }
    (async () => {
      setDeviceLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/device`, { params: { custCd: selectedCust } });
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setDeviceList(res.data.data || []);
        setSelectedDevice(null);
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setDeviceLoading(false);
      }
    })();
  }, [selectedCust]);

  const fetchData = async () => {
    if (!selectedCust) {
      setError("Please select CUST first.");
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    try {
      const params = {
        inCustCd: selectedCust,
        inDevice: selectedDevice || '%',
        inDateFrom: formatDate(dateFrom),
        inDateTo: formatDate(dateTo)
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R420`, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);

      const mappedData = (res.data.data || []).map((row, idx) => ({
        ...row,
        HOLD_TIME: formatOracleDateTimeString(row.HOLD_TIME),
        RELEASE_TIME: formatOracleDateTimeString(row.RELEASE_TIME),
        id: `${row.LOT_NO}-${row.SUB || ''}-${idx}`,
      }));
      setData(mappedData);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류가 발생했습니다.";
      setError(`조회 오류: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("HOLD_RELEASE_LOT");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "HOLD_RELEASE_LOT.xlsx");
      });
    });
    e.cancel = true;
  };

  return (
    <div style={{ padding: 0, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>HOLD/RELEASE LOT LIST</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 15, alignItems: "center" }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedCust(e.value)} disabled={comboLoading || user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} disabled={deviceLoading || !selectedCust} searchEnabled showClearButton placeholder="DEVICE (ALL)" label="DEVICE" />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "red", fontWeight: 'bold' }}>{error}</p>}
      </div>

      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}> {/* [수정] 중복 div 제거 및 position: 'relative' 추가 */}
        {/* 로딩 인디케이터를 그리드 영역 안으로 이동 */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
          dataSource={data} 
          keyExpr="id" 
          onExporting={onExporting}
          height="100%"
          showBorders={true} 
          columnAutoWidth={true} 
          allowColumnResizing={true} 
          hoverStateEnabled={true}
        >
          <Export enabled={true} allowExportSelectedData={false} />
          <Paging defaultPageSize={10} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} highlightCaseSensitive={false} />
          <Sorting mode="multiple" />
          
          <Column dataField="CUST_CD" caption="CUST" fixed={true}/>
          <Column dataField="DEVICE" caption="DEVICE" fixed={true} />
          <Column dataField="LOT_NO" caption="LOT NO" fixed={true}/>
          <Column dataField="SUB" caption="SUB" fixed={true} />
          <Column dataField="DUP" caption="DUP" fixed={true}/>
          <Column dataField="HOLD_TIME" caption="HOLD TIME" fixed={true}/>
          <Column dataField="RELEASE_TIME" caption="RELEASE TIME" fixed={true}/>
          <Column dataField="HOLD_CD" caption="HOLD CODE" />
          <Column dataField="HOLD_COMMENT" caption="HOLD COMMENT" width={200} />
          <Column dataField="RELEASE_COMMENT" caption="RELEASE COMMENT" width={200} />
          <Column dataField="CT" caption="C/T" />
          <Column dataField="LOT_QTY" caption="LOT QTY" dataType="number" alignment="right" />
          <Column dataField="IQC" caption="IQC" dataType="number" alignment="right" />
          <Column dataField="PTST" caption="PTST" dataType="number" alignment="right" />
          <Column dataField="PBKE" caption="PBKE" dataType="number" alignment="right" />
          <Column dataField="PTST2" caption="PTST2" dataType="number" alignment="right" />
          <Column dataField="UVE" caption="UVE" dataType="number" alignment="right" />
          <Column dataField="PTST3" caption="PTST3" dataType="number" alignment="right" />
          <Column dataField="QEI" caption="QEI" dataType="number" alignment="right" />
          <Column dataField="FQC" caption="FQC" dataType="number" alignment="right" />
          <Column dataField="WPK" caption="WPK" dataType="number" alignment="right" />
          <Column dataField="QPI" caption="QPI" dataType="number" alignment="right" />
          <Column dataField="FGS" caption="FGS" dataType="number" alignment="right" />
          <Column dataField="SHIP" caption="SHIP" dataType="number" alignment="right" />
        </DataGrid>
      </div>
    </div>
  );
}